import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Wrench, MapPin, Calendar,
  CheckCircle, AlertTriangle, Clock, FileText, User,
  Activity, ClipboardList, BookOpen, Loader2,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const statusColors = {
  completed: 'bg-emerald-100 text-emerald-700',
  planned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
  waiting_parts: 'bg-purple-100 text-purple-700',
};

const statusLabels = {
  planned: 'Planificata', in_progress: 'In lucru',
  completed: 'Finalizata', cancelled: 'Anulata',
  waiting_parts: 'Asteapta piese',
  open: 'Deschis', accepted: 'Acceptat',
  resolved: 'Rezolvat', closed: 'Inchis',
};

const prioColors = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-amber-100 text-amber-600',
  urgent: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-100 text-red-600',
};

const formatDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && d.length >= 10) return d.slice(0, 10);
  return '-';
};

const PdfCanvasViewer = ({ pdfData, isLoading, title, initialPage }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(initialPage || 1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const observerRef = useRef(null);

  // Create a fresh copy each render to avoid ArrayBuffer detach issues
  const fileProp = React.useMemo(() => {
    if (!pdfData) return null;
    return { data: new Uint8Array(pdfData) };
  }, [pdfData]);

  // Update initial page when prop changes
  useEffect(() => {
    if (initialPage && initialPage > 0) {
      setPageNumber(initialPage);
    }
  }, [initialPage]);

  // Callback ref to measure container width reliably
  const containerRefCallback = useCallback((node) => {
    // Clean up old observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const w = node.clientWidth - 8;
      if (w > 0) setContainerWidth(w);

      const ro = new ResizeObserver(() => {
        const newW = node.clientWidth - 8;
        if (newW > 0) setContainerWidth(newW);
      });
      ro.observe(node);
      observerRef.current = ro;
    }
  }, []);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mb-3 text-emerald-600" />
        <p className="text-sm">Se genereaza {title}...</p>
      </div>
    );
  }

  if (!pdfData || loadError) {
    return (
      <div className="text-center py-16 text-slate-400">
        <FileText className="w-12 h-12 mx-auto mb-3" />
        <p>{loadError ? 'Eroare la incarcarea PDF-ului' : 'Nu s-a putut genera PDF-ul'}</p>
      </div>
    );
  }

  const effectiveWidth = containerWidth > 0 ? containerWidth * scale : undefined;

  return (
    <div ref={containerRefCallback} data-testid={`pdf-viewer-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Controls */}
      <div className="flex items-center justify-between bg-slate-800 text-white rounded-t-lg px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
            className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30"
            data-testid="pdf-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm px-2 min-w-[80px] text-center">
            {pageNumber} / {numPages || '...'}
          </span>
          <button
            onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))}
            disabled={pageNumber >= (numPages || 1)}
            className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30"
            data-testid="pdf-next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setScale(s => Math.max(0.5, +(s - 0.1).toFixed(1)))}
            className="p-1.5 rounded hover:bg-slate-700"
            data-testid="pdf-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs px-2 min-w-[48px] text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale(s => Math.min(2.5, +(s + 0.1).toFixed(1)))}
            className="p-1.5 rounded hover:bg-slate-700"
            data-testid="pdf-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* PDF Canvas */}
      <div className="border border-t-0 border-slate-200 rounded-b-lg bg-slate-100 overflow-auto" style={{ maxHeight: '75vh' }}>
        <div className="flex justify-center py-2">
          {containerWidth > 0 && (
            <Document
              file={fileProp}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoadError(false); }}
              onLoadError={(err) => { console.error('PDF load error:', err); setLoadError(true); }}
              loading={
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={effectiveWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          )}
        </div>
      </div>
    </div>
  );
};

const AuditEquipmentDetailPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('grafic');
  const [graficPdfData, setGraficPdfData] = useState(null);
  const [planPdfData, setPlanPdfData] = useState(null);
  const [graficLoading, setGraficLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [planInitialPage, setPlanInitialPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/audit-portal/equipment/${code}`);
      setData(res.data);
    } catch (err) {
      toast.error('Eroare la incarcarea datelor');
      navigate('/audit');
    } finally {
      setLoading(false);
    }
  }, [code, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load Grafic PDF as ArrayBuffer
  useEffect(() => {
    if (activeTab === 'grafic' && !graficPdfData && data) {
      const loadGrafic = async () => {
        try {
          setGraficLoading(true);
          const year = new Date().getFullYear();
          const equipmentId = data.equipment.id;
          const res = await api.get(`/preventive/grafic/pdf?equipment_id=${equipmentId}&year=${year}`, {
            responseType: 'arraybuffer'
          });
          setGraficPdfData(Array.from(new Uint8Array(res.data)));
        } catch (err) {
          console.error('Error loading grafic PDF:', err);
          toast.error('Eroare la generarea Graficului de Intretinere');
        } finally {
          setGraficLoading(false);
        }
      };
      loadGrafic();
    }
  }, [activeTab, graficPdfData, data]);

  // Load Plan PDF + find equipment page
  useEffect(() => {
    if (activeTab === 'plan' && !planPdfData && data) {
      const loadPlan = async () => {
        try {
          setPlanLoading(true);
          const year = new Date().getFullYear();
          const equipmentName = data.equipment.name;
          
          // Load PDF and find page in parallel
          const [pdfRes, pageRes] = await Promise.all([
            api.get(`/audit-portal/plan-mentenanta-pdf?year=${year}`, { responseType: 'arraybuffer' }),
            api.get(`/audit-portal/plan-equipment-page?equipment_name=${encodeURIComponent(equipmentName)}&year=${year}`)
          ]);
          
          setPlanPdfData(Array.from(new Uint8Array(pdfRes.data)));
          if (pageRes.data.found) {
            setPlanInitialPage(pageRes.data.page);
          }
        } catch (err) {
          console.error('Error loading plan PDF:', err);
          toast.error('Eroare la generarea Planului de Mentenanta');
        } finally {
          setPlanLoading(false);
        }
      };
      loadPlan();
    }
  }, [activeTab, planPdfData, data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!data) return null;

  const { equipment, revisions, tickets, stats } = data;

  const tabs = [
    { id: 'grafic', label: 'Grafic Intretinere', icon: ClipboardList },
    { id: 'plan', label: 'Plan Mentenanta', icon: BookOpen },
    { id: 'revisions', label: `Revizii (${revisions.length})`, icon: FileText },
    { id: 'tickets', label: `Tichete (${tickets.length})`, icon: AlertTriangle },
  ];

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8" data-testid="audit-equipment-detail">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/audit')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Inapoi
        </Button>
      </div>

      {/* Equipment Info Card */}
      <Card className="mb-4 border-0 shadow-sm bg-white">
        <CardContent className="p-4 lg:p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Wrench className="w-7 h-7 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-slate-900" data-testid="equipment-name">{equipment.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{equipment.code}</span>
                {equipment.location_name && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {equipment.location_name}
                  </span>
                )}
                {equipment.manufacturer && (
                  <span>{equipment.manufacturer} {equipment.model || ''}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {equipment.serial_number && <Badge variant="outline" className="text-xs">Serie: {equipment.serial_number}</Badge>}
                {equipment.year_manufactured && <Badge variant="outline" className="text-xs">An: {equipment.year_manufactured}</Badge>}
                <Badge variant={equipment.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                  {equipment.status === 'active' ? 'Activ' : equipment.status}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 lg:p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total_revisions}</p>
            <p className="text-xs text-slate-500">Total revizii</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 lg:p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.completed_revisions}</p>
            <p className="text-xs text-slate-500">Finalizate</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 lg:p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.completion_rate}%</p>
            <p className="text-xs text-slate-500">Rata finalizare</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 lg:p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.total_tickets}</p>
            <p className="text-xs text-slate-500">Tichete</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-[120px] py-2.5 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grafic Intretinere */}
      {activeTab === 'grafic' && (
        <PdfCanvasViewer
          pdfData={graficPdfData}
          isLoading={graficLoading}
          title="Grafic Intretinere"
        />
      )}

      {/* Plan Mentenanta - with auto-navigation to equipment page */}
      {activeTab === 'plan' && (
        <PdfCanvasViewer
          pdfData={planPdfData}
          isLoading={planLoading}
          title="Plan Mentenanta"
          initialPage={planInitialPage}
        />
      )}

      {/* Revisions List */}
      {activeTab === 'revisions' && (
        <div className="space-y-2">
          {revisions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nicio revizie inregistrata</p>
            </div>
          ) : revisions.map((rev, idx) => (
            <Card key={rev.id || idx} className="border-0 shadow-sm bg-white">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900 text-sm lg:text-base">{rev.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[rev.status] || 'bg-slate-100 text-slate-600'}`}>
                        {statusLabels[rev.status] || rev.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(rev.scheduled_date)}
                      </span>
                      <span className="flex items-center gap-1 capitalize">
                        <Activity className="w-3 h-3" />
                        {rev.revision_type === 'preventive' ? 'Preventiva' :
                         rev.revision_type === 'corrective' ? 'Corectiva' : 'Predictiva'}
                      </span>
                      {rev.assigned_to_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {rev.assigned_to_name}
                        </span>
                      )}
                      {(rev.actual_hours || rev.estimated_hours) && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {rev.actual_hours || rev.estimated_hours}h
                        </span>
                      )}
                    </div>
                  </div>
                  {rev.status === 'completed' && (
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tickets List */}
      {activeTab === 'tickets' && (
        <div className="space-y-2">
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Niciun tichet inregistrat</p>
            </div>
          ) : tickets.map((t, idx) => (
            <Card key={t.id || idx} className="border-0 shadow-sm bg-white">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-400">#{t.ticket_number}</span>
                      <h3 className="font-semibold text-slate-900 text-sm lg:text-base">{t.title}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(t.created_at)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[t.status] || 'bg-slate-100 text-slate-600'}`}>
                        {statusLabels[t.status] || t.status}
                      </span>
                      {t.priority && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${prioColors[t.priority] || ''}`}>
                          {t.priority === 'low' ? 'Scazuta' : t.priority === 'medium' ? 'Medie' :
                           t.priority === 'high' ? 'Ridicata' : t.priority === 'urgent' ? 'Urgenta' : 'Critica'}
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-xs text-slate-500 mt-2 line-clamp-2">{t.description}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditEquipmentDetailPage;
